import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export interface Notification {
    id: string;
    title: string;
    message: string;
    type: 'application' | 'system' | 'info';
    timestamp: Date;
    read: boolean;
    /** Si están definidos, la UI debe mostrar t(titleKey) / t(messageKey) para traducir según idioma */
    titleKey?: string;
    messageKey?: string;
    vacanteId?: string;
    /** Información detallada para tipo application (campana + Config > Notificaciones) */
    vacanteTitle?: string;
    applicationId?: string;
    candidateName?: string;
    candidateEmail?: string;
}

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const STORAGE_KEY_RECRUITER = 'air_notifications';
export const STORAGE_KEY_ADMIN = 'air_notifications_admin';

const WELCOME_ADMIN_TITLE = 'Panel de administración';
const WELCOME_RECRUITER_TITLE = 'Bienvenido a AI Recruiter';

function migrateNotificationKeys(n: any, key: string): any {
    const out = { ...n, timestamp: new Date(n.timestamp) };
    if (out.titleKey != null && out.messageKey != null) return out;
    if (key === STORAGE_KEY_ADMIN && out.title === WELCOME_ADMIN_TITLE) {
        out.titleKey = 'notification.welcomeAdminTitle';
        out.messageKey = 'notification.welcomeAdminMessage';
    } else if (key === STORAGE_KEY_RECRUITER && out.title === WELCOME_RECRUITER_TITLE) {
        out.titleKey = 'notification.welcomeRecruiterTitle';
        out.messageKey = 'notification.welcomeRecruiterMessage';
    }
    return out;
}

function loadFromStorage(key: string): Notification[] {
    try {
        const saved = localStorage.getItem(key);
        if (saved) {
            const parsed = JSON.parse(saved);
            return (parsed || []).map((n: any) => migrateNotificationKeys(n, key));
        }
    } catch (e) {
        console.error('Error loading notifications:', e);
    }
    return [];
}

export const NotificationProvider: React.FC<{ children: React.ReactNode; storageKey?: string }> = ({ children, storageKey: storageKeyProp }) => {
    const storageKey = storageKeyProp ?? STORAGE_KEY_RECRUITER;

    const [notifications, setNotifications] = useState<Notification[]>(() => loadFromStorage(storageKey));

    useEffect(() => {
        setNotifications(loadFromStorage(storageKey));
    }, [storageKey]);

    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify(notifications));
    }, [notifications, storageKey]);

    const unreadCount = notifications.filter(n => !n.read).length;

    const addNotification = useCallback((n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
        const newNotification: Notification = {
            ...n,
            id: Math.random().toString(36).substring(2, 9),
            timestamp: new Date(),
            read: false,
        };
        setNotifications(prev => [newNotification, ...prev]);
        console.log('New Notification:', newNotification.title);
    }, []);

    const markAsRead = useCallback((id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, []);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    useEffect(() => {
        if (notifications.length === 0) {
            const welcome = storageKey === STORAGE_KEY_ADMIN
                ? { title: 'Panel de administración', message: 'Aquí verás notificaciones de la plataforma (nuevas aplicaciones, etc.).', titleKey: 'notification.welcomeAdminTitle', messageKey: 'notification.welcomeAdminMessage', type: 'system' as const }
                : { title: 'Bienvenido a AI Recruiter', message: 'Saity te ayudará durante todo tu proceso con IA.', titleKey: 'notification.welcomeRecruiterTitle', messageKey: 'notification.welcomeRecruiterMessage', type: 'system' as const };
            addNotification(welcome);
        }
    }, [storageKey, addNotification, notifications.length]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            addNotification,
            markAsRead,
            markAllAsRead,
            clearNotifications
        }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
